<template>
  <v-container>
    <v-card class="pa-8" max-width="720">
      <div v-if="mode === 'form'">
        <h1 class="text-h5 font-weight-bold mb-1">Notificar actualización de Términos y Condiciones</h1>
        <p class="text-body-2 grey--text text--darken-1 mb-6">
          Se enviará un correo a todos los miembros con membresía activa avisando la actualización.
        </p>

        <v-form ref="form" v-model="formValid">
          <v-text-field
            v-model="form.link"
            label="Nuevo link de Términos y Condiciones"
            :rules="linkRules"
            :disabled="launching"
            outlined
            dense
          />
          <v-textarea
            v-model="form.message"
            label="Mensaje del correo"
            :rules="messageRules"
            :disabled="launching"
            outlined
            rows="4"
          />

          <v-btn color="primary" :loading="launching" :disabled="launching" @click="submit">
            Enviar notificación
          </v-btn>
        </v-form>
      </div>

      <div v-else>
        <h1 class="text-h5 font-weight-bold mb-1">Notificación de Términos y Condiciones</h1>
        <v-chip :color="statusColor" dark class="mb-4">{{ statusLabel }}</v-chip>

        <div class="pw-label">Link</div>
        <div class="pw-value mb-3">{{ run.link }}</div>

        <div class="pw-label">Mensaje</div>
        <div class="pw-value mb-3">{{ run.message }}</div>

        <v-progress-linear :value="progressPercent" height="20" color="primary" class="mb-2">
          <template v-slot:default>{{ run.sentCount }} / {{ run.targetTotal }}</template>
        </v-progress-linear>

        <p v-if="run.failedCount" class="text-caption grey--text text--darken-1">
          {{ run.failedCount }} envíos fallidos (no bloquean el resto).
        </p>

        <v-btn v-if="!isFinal" color="error" text :loading="cancelling" :disabled="cancelling" @click="cancel">
          Cancelar
        </v-btn>

        <v-btn v-if="isFinal" color="primary" text @click="resetToForm">
          Enviar una nueva notificación
        </v-btn>
      </div>
    </v-card>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { utilsMixin } from '../mixins/utilsMixin';
import TermsNotificationService from '../services/terms-notification.service';

const STORAGE_KEY = 'termsNotificationRunId';
const POLL_INTERVAL_MS = 10000;
const FINAL_STATUSES = ['completed', 'failed', 'cancelled'];
const STATUS_LABELS = {
  pending: 'Pendiente',
  running: 'Enviando',
  completed: 'Completado',
  failed: 'Falló',
  cancelled: 'Cancelado'
};
const STATUS_COLORS = {
  pending: 'grey',
  running: 'primary',
  completed: 'success',
  failed: 'error',
  cancelled: 'grey darken-1'
};

export default {
  name: 'TermsNotification',
  mixins: [utilsMixin],
  data() {
    return {
      mode: 'form',
      form: {
        link: '',
        message: ''
      },
      linkRules: [
        (v) => !!v || 'El link es obligatorio',
        (v) => /^https?:\/\/.+/.test(v || '') || 'Debe ser un link válido'
      ],
      messageRules: [
        (v) => !!v || 'El mensaje es obligatorio',
        (v) => (v || '').length <= 2000 || 'Máximo 2000 caracteres'
      ],
      formValid: false,
      launching: false,
      cancelling: false,
      run: null,
      pollHandle: null,
      // Snackbar (utilsMixin)
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'red'
    };
  },
  computed: {
    isFinal() {
      return !!this.run && FINAL_STATUSES.includes(this.run.status);
    },
    progressPercent() {
      if (!this.run || !this.run.targetTotal) return 0;
      return Math.min(100, Math.round((this.run.sentCount / this.run.targetTotal) * 100));
    },
    statusLabel() {
      return this.run ? (STATUS_LABELS[this.run.status] || this.run.status) : '';
    },
    statusColor() {
      return this.run ? (STATUS_COLORS[this.run.status] || 'grey') : 'grey';
    }
  },
  async mounted() {
    const storedRunId = this.getStoredRunId();
    if (!storedRunId) return;

    const res = await TermsNotificationService.getStatus(storedRunId);
    if (res.status === 'ok') {
      this.run = res.data;
      this.mode = 'status';
      if (this.isFinal) {
        this.clearStoredRunId();
      } else {
        this.startPolling();
      }
    } else if (res.code === 'TERMS_NOTIFICATION_RUN_NOT_FOUND') {
      this.clearStoredRunId();
    }
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    getStoredRunId() {
      try {
        return localStorage.getItem(STORAGE_KEY);
      } catch (error) {
        return null;
      }
    },
    setStoredRunId(id) {
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch (error) {
        // localStorage no disponible (ventana privada, storage bloqueado) — no bloquea el flujo
      }
    },
    clearStoredRunId() {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        // ver setStoredRunId
      }
    },
    async submit() {
      if (!this.$refs.form.validate()) return;

      this.launching = true;
      const res = await TermsNotificationService.launch(this.form);
      this.launching = false;

      if (res.status === 'ok') {
        this.run = res.data;
        this.setStoredRunId(res.data.id);
        this.mode = 'status';
        this.startPolling();
        return;
      }

      if (res.code === 'TERMS_NOTIFICATION_ALREADY_IN_PROGRESS' && res.data && res.data.existingRunId) {
        this.setStoredRunId(res.data.existingRunId);
        const statusRes = await TermsNotificationService.getStatus(res.data.existingRunId);
        if (statusRes.status === 'ok') {
          this.run = statusRes.data;
          this.mode = 'status';
          if (!this.isFinal) this.startPolling();
        }
        this.showErrorSnackbar('Ya hay una notificación en curso. Mostrando su estado.');
        return;
      }

      this.showErrorSnackbar(res.message || 'No se pudo lanzar la notificación.');
    },
    async cancel() {
      this.cancelling = true;
      const res = await TermsNotificationService.cancel(this.run.id);
      this.cancelling = false;

      if (res.status === 'ok') {
        this.run = res.data;
        this.stopPolling();
        this.clearStoredRunId();
        return;
      }

      this.showErrorSnackbar(res.message || 'No se pudo cancelar la notificación.');
    },
    startPolling() {
      this.stopPolling();
      this.pollHandle = setInterval(this.poll, POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollHandle) {
        clearInterval(this.pollHandle);
        this.pollHandle = null;
      }
    },
    async poll() {
      const res = await TermsNotificationService.getStatus(this.run.id);
      if (res.status !== 'ok') {
        return;
      }

      this.run = res.data;
      if (this.isFinal) {
        this.stopPolling();
        this.clearStoredRunId();
      }
    },
    resetToForm() {
      this.mode = 'form';
      this.run = null;
      this.form = { link: '', message: '' };
      if (this.$refs.form) this.$refs.form.resetValidation();
    }
  }
};
</script>
